/**
 * Utility functions extracted from marketDetailPage.js
 * These functions have zero React dependencies and are pure computation/utils.
 */


const normalizeNumericArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return Array.from(
    new Set(
      value.map((item) => Number(item)).filter((item) => Number.isFinite(item)),
    ),
  ).sort((a, b) => a - b);
};

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set();
  const result = [];
  value.forEach((item) => {
    if (item === null || item === undefined) {
      return;
    }
    const normalized =
      typeof item === 'string' ? item.trim() : String(item).trim();
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
};

const arraysEqual = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    return Array.isArray(a) === Array.isArray(b);
  }

  if (a.length !== b.length) {
    return false;
  }

  return a.every((value, index) => value === b[index]);
};

const toTimestamp = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (!value && value !== 0) {
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};


// Trendline V2 was retired (DWLF-322) and V1 is the only served version, so the
// old per-symbol version preferences are meaningless. Saved chart preferences may
// still carry them; strip them on hydration so they are never applied. A symbol's
// stored entry sheds them the next time that symbol's preferences are saved.
const LEGACY_TRENDLINE_VERSION_PREFERENCE_KEYS = ['trendlineVersion', 'trendlineVersionByTimeframe'];

const stripLegacyTrendlineVersionPreferences = (preferences) => {
  if (!preferences || typeof preferences !== 'object') {
    return {};
  }
  const result = { ...preferences };
  LEGACY_TRENDLINE_VERSION_PREFERENCE_KEYS.forEach((key) => {
    delete result[key];
  });
  return result;
};

// Before DWLF-440 the cycle window bands followed the Cycle Lows / Cycle Highs pivot
// toggles. A symbol saved before the split has no window key, so it starts from the
// pivot key once, on hydration; the window key is then saved with the rest and the two
// toggles are independent from there on.
const LEGACY_CYCLE_WINDOW_SOURCES = {
  showCycleLowWindow: 'showWeeklyCycleLows',
  showCycleHighWindow: 'showWeeklyCycleHighs',
};

const migrateCycleWindowPreferences = (preferences) => {
  const result = { ...preferences };
  Object.entries(LEGACY_CYCLE_WINDOW_SOURCES).forEach(([windowKey, pivotKey]) => {
    if (typeof result[windowKey] !== 'boolean' && typeof result[pivotKey] === 'boolean') {
      result[windowKey] = result[pivotKey];
    }
  });
  return result;
};

const normalizeBooleanPreference = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'true') {
      return true;
    }
    if (lower === 'false') {
      return false;
    }
  }
  return undefined;
};

const BOOLEAN_CHART_PREFERENCE_KEYS = new Set([
  'showWeeklyTrendlines',
  'showDailyTrendlines',
  'showHourlyTrendlines',
  'showSwings',
  'showSystemTrades',
  'showUserTrades',
  'showTradeJournal',
  'showTrades',
  'showFib',
  'showSR',
  'showDSS',
  'showATR',
  'showRSI',
  'showADX',
  'showStages',
  'showBollinger',
  'showEMACloud',
  'showWeeklyCycleLows',
  'showWeeklyCycleHighs',
  'showLiveWeeklyCycleLows',
  'showLiveWeeklyCycleHighs',
  'showCycleLowWindow',
  'showCycleHighWindow',
  'showPastLowWindows',
  'showPastHighWindows',
  'showCycleTrendlines',
]);

const STRING_ARRAY_CHART_PREFERENCE_KEYS = new Set([
  // DWLF-447: the layers whose event markers are switched on (per-indicator, all off by default).
  'eventLayers',
  'customEventSelections',
  'userStrategySelections',
  // VWAP anchor periods ('month' | 'quarter' | 'year') — a string
  // array, so it must NOT be routed through normalizeNumericArray
  // (which would coerce the anchors to NaN and drop them).
  'vwapAnchors',
  // SMC overlay families the user has toggled on ('fvg' | 'orderBlock' |
  // 'structure' | ...). Same as vwapAnchors: a string array that must persist
  // so the chart reopens with the SMC parts the user left enabled.
  'smcOverlays',
]);

const normalizeSymbolKey = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed.replace(/\//g, '-');
};

const createSymbolVariants = (value) => {
  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const variants = new Set([trimmed]);

  if (trimmed.includes('/')) {
    variants.add(trimmed.replace(/\//g, '-'));
  }
  if (trimmed.includes('-')) {
    variants.add(trimmed.replace(/-/g, '/'));
  }

  return Array.from(variants);
};

const objectsShallowEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
};

const shallowPreferencesEqual = (left = {}, right = {}) => {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => {
    const leftValue = left[key];
    const rightValue = right[key];

    if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
      return arraysEqual(leftValue, rightValue);
    }

    if (
      leftValue &&
      rightValue &&
      typeof leftValue === 'object' &&
      typeof rightValue === 'object' &&
      !Array.isArray(leftValue) &&
      !Array.isArray(rightValue)
    ) {
      return objectsShallowEqual(leftValue, rightValue);
    }

    return leftValue === rightValue;
  });
};

// Reconcile a user's saved custom-event chart selections against the set of
// custom-event ids that currently have a definition.
//
// The "My Data → Custom Events (N)" count, the My Data nav badge, and the
// fire-fetch the chart triggers are all driven by the selected ids. A selection
// for an event whose definition has since been DELETED must be dropped — left in
// place it keeps the count at (1), the badge lit, and re-fetches the deleted
// event's now-orphaned fires so its markers keep rendering.
//
// The catch is the async load gap: on mount the definition list is briefly empty
// before the fetch resolves, and we must NOT clobber the saved selections during
// that window. So we only prune once we KNOW the definition set is authoritative
// — i.e. when at least one definition is known, OR the caller signals the fetch
// has completed (`definitionsLoaded`). Until then, saved selections pass through
// untouched.
const reconcileCustomEventSelections = (
  savedSelections,
  definitionIdSet,
  definitionsLoaded = false,
) => {
  const ids = Array.isArray(savedSelections) ? savedSelections : [];
  const idSet = definitionIdSet instanceof Set
    ? definitionIdSet
    : new Set(Array.isArray(definitionIdSet) ? definitionIdSet : []);

  // Definitions not yet authoritative (none known and the fetch hasn't
  // completed) → keep the saved selections so the async gap can't wipe them.
  if (idSet.size === 0 && !definitionsLoaded) {
    return [...ids];
  }

  return ids.filter((id) => idSet.has(id));
};

// The chart's timeframe pills for a symbol, shortest to longest (1H · Daily · Weekly). Daily and weekly
// are on unless the symbol turns them off; hourly only when it says so.
const timeframeOptionsFor = (supported = {}) => {
  const options = [];
  if (supported['1h']) options.push('hourly');
  if (supported.daily !== false) options.push('daily');
  if (supported.weekly !== false) options.push('weekly');
  return options.length ? options : ['daily'];
};

// Where the chart lands when the current timeframe isn't offered: daily when it is, whatever the pill
// order, else the first option.
const fallbackTimeframe = (options) => (options.includes('daily') ? 'daily' : options[0]);

// Export all utility functions
export {
  timeframeOptionsFor,
  fallbackTimeframe,
  normalizeNumericArray,
  normalizeStringArray,
  reconcileCustomEventSelections,
  arraysEqual,
  toTimestamp,
  stripLegacyTrendlineVersionPreferences,
  migrateCycleWindowPreferences,
  normalizeBooleanPreference,
  BOOLEAN_CHART_PREFERENCE_KEYS,
  STRING_ARRAY_CHART_PREFERENCE_KEYS,
  normalizeSymbolKey,
  createSymbolVariants,
  objectsShallowEqual,
  shallowPreferencesEqual,
};